// ============================================================
// THE COMPANY — Fetch wrapper for the /api/* backend
// ============================================================

export const TOKEN_KEY = "the-company-token";

function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

function handleUnauthorized(): never {
  sessionStorage.removeItem(TOKEN_KEY);
  window.location.reload();
  // The reload will prevent further execution, but TS needs a `never` return.
  throw new Error("Unauthorized — session cleared");
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const method = options.method ?? "GET";

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> | undefined),
  };

  if (method !== "GET" && method !== "HEAD") {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(path, {
    ...options,
    headers,
  });

  if (res.status === 401) {
    handleUnauthorized();
  }

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`API ${method} ${path} failed (${res.status}): ${body}`);
  }

  if (res.status === 204 || res.headers.get("content-length") === "0") {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

export const api = {
  get<T>(path: string): Promise<T> {
    return request<T>(path, { method: "GET" });
  },

  post<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, {
      method: "POST",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  },

  put<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, {
      method: "PUT",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  },

  del<T>(path: string): Promise<T> {
    return request<T>(path, { method: "DELETE" });
  },
};
